import React, { PropsWithChildren, useRef, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import './CardFinder.css';
import { wrapTrademarkSuperscript } from '../../utils/cob-utils';
import { Section, Row, Col, Button } from '../../importCL';
import InputCheckbox from '../../component-library/atoms/InputCheckbox';
interface IResponse {
    redirectLink: string
    responseIcon: string
    responseText: string
}

interface IResponseSet {
    expectedPreviousResponse: string
    responses: IResponse[]
}

interface IQuestion {
    question: string
    reason: string
    responseSet: IResponseSet[]
}

interface ICardFinderProps extends MappedComponentProperties {
    questions?: IQuestion[]
    icons?: any
    backButton?: string;
    cancelButton?: 'NEXT' | 'CANCEL' | undefined;
    cancelButtonLink?: string;
}

export const CardFinderEditConfig = {
    emptyLabel: 'Card Finder',
    isEmpty: (props: PropsWithChildren<ICardFinderProps>) => {
        return !props || !props.questions;
    },
};

const CardFinder = (props: PropsWithChildren<ICardFinderProps>): JSX.Element => {

    const [page, setPage] = useState(1);
    const [canContinue, setCanContinue] = useState(false);
    const [redirect, setRedirect] = useState('none');
    const [picked, setPicked] = useState('');
    const [slideDownActive, setSlideDownActive] = useState<boolean>(false);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const responseStack = useRef<string[]>([]);
    const questionStack = useRef<object[]>([]);
    const wentBack = useRef<boolean>(false);
    const previousPage = useRef<number>(0);

    let reason;
    if (props?.questions !== undefined) {
        if (props.questions?.[page - 1] !== undefined) {
            reason = {
                title: 'Why do we ask this ',
                content: props.questions[page - 1]?.reason,
            };
        }
    }
    
    const updatePage = (arg) => {
        if (arg === 'prev') {
            setPage(page - 1);
            responseStack.current.pop();
            setSelectedIndex(null);
        } else if ( arg === 'next') {
            setPage(page + 1);
            responseStack.current.push(picked);
            setCanContinue(false);
        }
    };

    const useGetResponses = () => {
        let iconPath: string = '';
        let icons: any[] = [];
        let index2 = 0;

        if (props?.questions && props?.questions?.[page - 1] && props?.questions?.[page - 1]?.responseSet !== null) {
            if (props?.icons) {
                const currentQuestion = props.questions[page - 1];
                currentQuestion?.responseSet?.forEach(() => {
                    if ('' in props.icons?.[(page - 1).toString()]) {
                        icons = props.icons?.[(page - 1).toString()][''];
                    } else if (responseStack?.current?.[responseStack?.current?.length - 1] in props.icons?.[(page - 1).toString()]) {
                        icons = props.icons[(page - 1).toString()][responseStack.current[responseStack.current.length - 1]];
                    }
                });
                for (let i = 0; i < currentQuestion.responseSet.length; i++) {
                    if (currentQuestion.responseSet[i].expectedPreviousResponse === ''
                        || currentQuestion.responseSet[i].expectedPreviousResponse === responseStack.current[responseStack.current.length - 1]) {
                        index2 = i;
                        break;
                    }
                }
            }
        }

        icons = icons.map((icon, index) => {
            iconPath = icon?.iconPath;
            let text = '';
            if (props !== undefined && props?.questions !== undefined) {
                text = props.questions?.[page - 1]?.responseSet?.[index2]?.responses?.[index]?.responseText;
            }

            return <button className='border-none bg-transparent' key={index} onClick={() => {
                setSelectedIndex(index);
                setPicked(text);
                setCanContinue(true);
                if (props?.questions !== undefined && props?.questions?.[page - 1]?.responseSet?.[index2]?.responses[index]?.redirectLink !== null) {
                    setRedirect(props.questions[page - 1].responseSet[index2].responses[index].redirectLink);
                } else {
                    setRedirect('none');
                }
            }}>
                <InputCheckbox
                    className='!pl-4 m-0'
                    textClass={`text-base`}
                    label= {transformHTMLParserLinks(wrapTrademarkSuperscript(text))}
                    setValue={() => setSelectedIndex(index)}
                    variant='quiz'
                    iconPath={iconPath}
                    key={index} 
                    value={selectedIndex === index}
                />
            </button>;
        });


        if (!wentBack.current) {
            if (page !== previousPage.current) {
                questionStack.current.push(icons);
                previousPage.current = page;
            }
            return icons;
        } 
        wentBack.current = !wentBack.current;
        questionStack.current.pop();
        responseStack.current.pop();
        previousPage.current = page;
        return questionStack.current[questionStack.current.length - 1];
        
    };
    return (<div id='card-finder'className='CardFinder'>
        <Section className='!py-20'>
            <div className='text-grey-tundora md:text-s text-center mb-2'>
                {`Question ${page} of ${props.questions?.length}`}
            </div>
            <Row>
                <Col className='mb-8 text-center'>
                    <div className='CardFinder__questionTitle'>{props.questions && transformHTMLParserLinks(props.questions[page - 1].question)}</div>
                </Col>
            </Row>
            <Row className='flex flex-col items-center'>
                <Col className='CardFinder__icon flex flex-col max-w-[43%] mb-10'>
                    {useGetResponses()}
                </Col>
            </Row>
            <div className='flex justify-center'>
                {page > 1 &&
                    <div className='pr-4'>
                        <Button size='large'
                            variant='ghosted-dark'
                            className='px-8'
                            onClick={() => {
                                updatePage('prev');
                            }}>Previous</Button>
                    </div>
                }
                <Button size='large'
                    variant={canContinue ? 'solid' : 'inactive'}
                    disabled={!canContinue}
                    className='!px-12'
                    onClick={() => {
                        setSelectedIndex(null);
                        if (redirect !== 'none') {
                            window.location.assign(redirect);
                        }
                        if (props?.questions && props?.questions?.length > page) {
                            updatePage('next');
                        }
                    }}>Next</Button>
            </div>
            <Row>
                <Col xs={6} sm={6} md={12} lg={12} className='flex justify-center text-center mt-6'>
                    <ul className='CardFinder__accordion max-w-[43%] ml-0'>
                        {reason && (
                            <li className={`CardFinder__accordionItem overflow-hidden max-h-12 ${slideDownActive ? 'active' : 'inactive'}`}>
                                <button 
                                    className={`CardFinder__accordionButton`}
                                    aria-expanded={slideDownActive ? 'true' : 'false'}
                                    onClick={() => {
                                        setSlideDownActive(active => !active);
                                    }}
                                >
                                    <h2 className='text-blue-creditone font-normal text-base mb-0 mr-2'>{reason.title}</h2>
                                    <span className={`CardFinder__accordionArrow ml-0 pl-2 ${slideDownActive ? 'active' : 'inactive'}`} />
                                </button>
                                <article className={`pl-2`}>
                                    <article className='text-s pt-0 pr-6 pb-5 pl-0'>
                                        {transformHTMLParserLinks(reason.content)}
                                    </article>
                                </article>
                            </li>
                        )}
                    </ul>
                </Col>
            </Row>
        </Section>
    </div>);
};

export default CardFinder;
